/**
 * SSE stream reader for the chatbot API. Fetches the chat endpoint,
 * reads the event stream, and yields typed events for the provider
 * to consume. Pure function — no React dependencies.
 */

export type ChatStreamEvent =
  | { type: 'text_delta'; delta: string }
  | { type: 'tool_result'; toolName: string; result: Record<string, unknown> }
  | { type: 'done'; responseId: string }
  | { type: 'error'; message: string };

interface StreamChatBody {
  messages: Array<{ role: string; content: string }>;
  previousResponseId?: string;
}

/**
 * Fetches the chat SSE endpoint and yields parsed events.
 * Handles buffering, line splitting, and JSON parsing internally.
 */
export async function* streamChatResponse(
  url: string,
  body: StreamChatBody,
): AsyncGenerator<ChatStreamEvent> {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });

  if (!res.ok || !res.body) {
    yield { type: 'error', message: 'Request failed' };
    return;
  }

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;

    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split('\n\n');
    buffer = lines.pop() ?? '';

    for (const line of lines) {
      if (!line.startsWith('data: ')) continue;

      try {
        const event = JSON.parse(line.slice(6));
        yield event as ChatStreamEvent;
      } catch {
        // Skip malformed events
      }
    }
  }
}
